import { makeStyles } from "tss-react/mui"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faCircleInfo } from "@fortawesome/pro-regular-svg-icons"
import {
    Checkbox,
    FormControlLabel,
    Grid,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TextField,
    Typography,
} from "@mui/material"
import { useMoney } from "../../../../../currency/useMoney"
import { toDinero } from "../../../../../currency/currencyLib"
import { getOrderPerson } from "../../../../../../shared/utils/getOrderPerson"
import { MeetingServiceOrder } from "../meetingServiceTypes"
import { useDateTime } from "../../../../../localization/useDateTime"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserType } from "../../../../../context/contextSelectors"
import { UserType } from "../../../../../login/loginTypes"
import { Box } from "@mui/system"

const useStyles = makeStyles()((theme) => ({
    tableHead: {
        background: theme.palette.tailwind.slate[50],
    },
    tableHeadCell: {
        paddingTop: "0.75rem",
        paddingBottom: "0.75rem",
        color: theme.palette.tailwind.slate[500],
    },
    columnOrderId: {
        width: "10%",
    },
    columnDeliveryTime: {
        width: "25%",
        whiteSpace: "nowrap",
    },
    columnAmount: {
        width: "15%",
        whiteSpace: "nowrap",
    },
    infoBox: {
        width: "100%",
        display: "grid",
        gridTemplateColumns: "1.5rem 1fr",
        gap: "0.75rem",
        color: "#014361",
        background: "#E5F6FD",
        padding: "1rem",
        borderRadius: "0.25rem",
        justifySelf: "start",
    },
    infoBoxIcon: {
        color: "#0288D1",
        marginTop: "0.15rem",
    },
    checkboxesContainer: {
        background: theme.palette.tailwind.slate[100],
        padding: "0.5rem 1rem",
        borderRadius: "0.5rem",
    },
    table: {
        "& .MuiTableCell-stickyHeader": {
            background: theme.palette.tailwind.slate[50],
        },
    },
    tableContainer: {
        maxHeight: "32vh",
    },
    cancellationMessageField: {
        width: "100%",
        minWidth: "60%",
        marginBottom: "2rem",
    },
    singleOrderSummary: {
        display: "grid",
        gridTemplateColumns: "auto 1fr",
        rowGap: ".5rem",
        columnGap: "1.5rem",
        padding: ".5rem 0 1.5rem",
    },
    singleOrderLabel: {
        color: theme.palette.tailwind.slate[500],
    },
    singleOrderValue: {
        fontWeight: 500,
        color: theme.palette.tailwind.slate[900],
    },
}))

type MeetingServiceDeleteDialogContentProps = {
    list: MeetingServiceOrder[]
    checked: boolean
    handleChange: () => void
    hideBooking?: boolean
    hasDeleteMessage?: boolean
    cancellationMessage?: string
    setCancellationMessage?: (message: string) => void
    isProductionView?: boolean
}

const MAX_MESSAGE_LENGTH = 170

type OrderTableHeadProps = Readonly<{
    showPaymentColumns?: boolean
    withColumnWidths?: boolean
}>

function OrderTableHead({ showPaymentColumns = false, withColumnWidths = false }: OrderTableHeadProps) {
    const { classes, cx } = useStyles()

    return (
        <TableHead className={classes.tableHead}>
            <TableRow>
                <TableCell
                    className={
                        withColumnWidths ? cx(classes.tableHeadCell, classes.columnOrderId) : classes.tableHeadCell
                    }
                >
                    <LocalizedStrict id="meeting-service-table-order-id-column">
                        <>Order ID</>
                    </LocalizedStrict>
                </TableCell>
                <TableCell
                    className={
                        withColumnWidths ? cx(classes.tableHeadCell, classes.columnDeliveryTime) : classes.tableHeadCell
                    }
                >
                    <LocalizedStrict id="meeting-service-table-delivery-time-column">
                        <>Delivery time</>
                    </LocalizedStrict>
                </TableCell>
                <TableCell className={classes.tableHeadCell}>
                    <LocalizedStrict id="meeting-service-table-order-name-column">
                        <>Order name</>
                    </LocalizedStrict>
                </TableCell>
                {showPaymentColumns && (
                    <>
                        <TableCell className={classes.tableHeadCell}>
                            <LocalizedStrict id="meeting-service-table-status-column">
                                <>Status</>
                            </LocalizedStrict>
                        </TableCell>
                        <TableCell className={classes.tableHeadCell}>
                            <LocalizedStrict id="meeting-service-table-cash-payment-received-column">
                                <>Cash payment received</>
                            </LocalizedStrict>
                        </TableCell>
                    </>
                )}
                <TableCell
                    align="right"
                    className={withColumnWidths ? cx(classes.tableHeadCell, classes.columnAmount) : undefined}
                >
                    <LocalizedStrict id="meeting-service-table-amount-column">
                        <>Amount</>
                    </LocalizedStrict>
                </TableCell>
            </TableRow>
        </TableHead>
    )
}

type OrderNameCellProps = Readonly<{
    title: string | undefined
    subtitle: string | undefined
}>

function OrderNameCell({ title, subtitle }: OrderNameCellProps) {
    return (
        <Grid container flexDirection="column" gap={0.5}>
            <Typography variant="body2">{title}</Typography>
            <Typography variant="caption">{subtitle}</Typography>
        </Grid>
    )
}

export function MeetingServiceDeleteDialogContent({
    list,
    checked,
    hideBooking = false,
    hasDeleteMessage,
    handleChange,
    cancellationMessage,
    setCancellationMessage,
    isProductionView = false,
}: MeetingServiceDeleteDialogContentProps) {
    const { classes } = useStyles()
    const userType = useAppSelector(selectEffectiveUserType)
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})

    const remainingChars = MAX_MESSAGE_LENGTH - (cancellationMessage?.length || 0)
    const isSingleProductionOrder = isProductionView && list.length === 1

    return (
        <Grid width="100%">
            {!hideBooking && (
                <Grid container flexDirection="column" paddingBottom={4} gap={1} alignItems="center">
                    <Box className={classes.checkboxesContainer}>
                        <LocalizedStrict
                            id="meeting-service-delete-orders-checkbox-label-booking"
                            attrs={{ label: true }}
                        >
                            <FormControlLabel
                                control={<Checkbox checked={checked} onChange={handleChange} />}
                                label="Cancel resource booking"
                            />
                        </LocalizedStrict>
                    </Box>
                </Grid>
            )}
            <Grid container flexDirection="column" paddingBottom={3}>
                {userType !== UserType.USER && (
                    <Grid className={classes.infoBox}>
                        <FontAwesomeIcon icon={faCircleInfo} className={classes.infoBoxIcon} />
                        <Grid container flexDirection="column" gap={1.5}>
                            {!hideBooking ? (
                                <>
                                    <LocalizedStrict id="meeting-service-delete-orders-helper-line-one">
                                        <Typography variant="body2">
                                            OBS: If a resource booking is attached to the meeting service order, you can
                                            cancel the resource booking on behalf of the creator.
                                        </Typography>
                                    </LocalizedStrict>
                                    <LocalizedStrict id="meeting-service-delete-orders-helper-line-two">
                                        <Typography variant="body2">
                                            Be aware that if you cancel the resource booking, a possible reservation of
                                            rooms and associated AV equipment is also canceled and released to other
                                            users.
                                        </Typography>
                                    </LocalizedStrict>
                                </>
                            ) : (
                                <LocalizedStrict id="meeting-service-delete-orders-helper-merchant">
                                    <Typography variant="body2">
                                        Note: If a resource booking is attached to the meeting service order, it will
                                        not be canceled.
                                    </Typography>
                                </LocalizedStrict>
                            )}
                        </Grid>
                    </Grid>
                )}
            </Grid>
            {hasDeleteMessage && (
                <Box>
                    <LocalizedStrict
                        id="meeting-service-delete-orders-helper-merchant-cancellation-field"
                        attrs={{ label: true, placeholder: true, helperText: true }}
                        vars={{ charsRemaining: remainingChars }}
                    >
                        <TextField
                            className={classes.cancellationMessageField}
                            onChange={(e) => setCancellationMessage?.(e.target.value)}
                            name="message"
                            minRows={2}
                            maxRows={4}
                            multiline
                            inputProps={{ maxLength: MAX_MESSAGE_LENGTH }}
                            label="Cancellation message"
                            placeholder="Write a reason for cancellation. This message will be sent to the customer"
                            helperText={`Remaining characters: ${remainingChars}`}
                        />
                    </LocalizedStrict>
                </Box>
            )}
            {isSingleProductionOrder ? (
                (() => {
                    const order = list[0]

                    return (
                        <Box className={classes.singleOrderSummary}>
                            <LocalizedStrict id="meeting-service-delete-single-order-id-label">
                                <Typography variant="body2" className={classes.singleOrderLabel}>
                                    Order id:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="body2" className={classes.singleOrderValue}>
                                {order.id}
                            </Typography>
                            <LocalizedStrict id="meeting-service-delete-single-order-delivery-date-label">
                                <Typography variant="body2" className={classes.singleOrderLabel}>
                                    Delivery date:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="body2" className={classes.singleOrderValue}>
                                {dateTimeFactory.formatDate(dateTimeFactory.fromApi(order.deliveryDetails.time))}
                            </Typography>
                            <LocalizedStrict id="meeting-service-delete-single-order-meeting-name-label">
                                <Typography variant="body2" className={classes.singleOrderLabel}>
                                    Meeting name:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="body2" className={classes.singleOrderValue}>
                                {order.meetingDetails.meetingName}
                            </Typography>
                            <LocalizedStrict id="meeting-service-delete-single-order-responsible-label">
                                <Typography variant="body2" className={classes.singleOrderLabel}>
                                    Responsible:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="body2" className={classes.singleOrderValue}>
                                {getOrderPerson(order.organizers).responsible?.name ??
                                    order.customer?.displayName ??
                                    "-"}
                            </Typography>
                            <LocalizedStrict id="meeting-service-delete-single-order-amount-label">
                                <Typography variant="body2" className={classes.singleOrderLabel}>
                                    Amount:
                                </Typography>
                            </LocalizedStrict>
                            <Typography variant="body2" className={classes.singleOrderValue}>
                                {moneyFactory.format(toDinero(order.amount))}
                            </Typography>
                        </Box>
                    )
                })()
            ) : (
                <TableContainer className={classes.tableContainer}>
                    <Table stickyHeader aria-label="sticky table" className={classes.table}>
                        <OrderTableHead withColumnWidths />
                        <TableBody>
                            {list.map((order) => (
                                <TableRow key={order.id}>
                                    <TableCell className={classes.columnOrderId}>{order.id}</TableCell>
                                    <TableCell className={classes.columnDeliveryTime}>
                                        {dateTimeFactory.formatDateTime(
                                            dateTimeFactory.fromApi(order.deliveryDetails.time),
                                        )}
                                    </TableCell>
                                    <TableCell>
                                        <OrderNameCell
                                            title={order.meetingDetails.meetingName}
                                            subtitle={order.customer?.displayName}
                                        />
                                    </TableCell>
                                    <TableCell align="right">{moneyFactory.format(toDinero(order.amount))}</TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}
        </Grid>
    )
}

type MeetingServiceBookDialogContentProps = {
    list: MeetingServiceOrder[]
}

export function MeetingServiceBookDialogContent({ list }: MeetingServiceBookDialogContentProps) {
    const dateTimeFactory = useDateTime()
    const moneyFactory = useMoney({})

    return (
        <Grid>
            <Table sx={{ minWidth: 650 }}>
                <OrderTableHead showPaymentColumns />
                <TableBody>
                    {list.map((order) => (
                        <TableRow key={order.id}>
                            <TableCell>{order.id}</TableCell>
                            <TableCell>
                                {dateTimeFactory.formatDateTime(dateTimeFactory.fromApi(order.deliveryDetails.time))}
                            </TableCell>
                            <TableCell>
                                <OrderNameCell
                                    title={order.deliveries[0]?.orderLines[0]?.name}
                                    subtitle={order.customer.displayName}
                                />
                            </TableCell>
                            <TableCell></TableCell>
                            <TableCell></TableCell>
                            <TableCell align="right">{moneyFactory.format(toDinero(order.amount))}</TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>
        </Grid>
    )
}
